<template>
  <div class="relative w-full">
    <div class="flex flex-col lg:flex-row gap-3">
      <!-- Form Section -->
      <div
          class="w-full"
          :class="{
          'lg:w-1/2': showList && !fullWidth,
          'lg:w-full': !showList || fullWidth
        }"
      >
        <div
            class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md">
          <!-- Form Header -->
          <div v-if="showHeader"
               class="px-4 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <span class="text-xl font-bold text-purple-600 inline-block transition-all duration-500">
                <span class="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 hover:animate-pulse">
                  {{ title }}
                </span>
              </span>


              <span
                  v-if="showCreationData && (item.created_by || item.created_at) && !item.is_system_predefined"
                  class="px-2 py-1 text-xs text-pink-400 flex items-center gap-1"
              >
                <span v-if="item.created_by" class="font-semibold">
                  {{ item.created_by.name || item.created_by }}
                </span>
                <span v-if="item.created_at" class="text-xs">
                  در {{ item.created_at }}
                </span>
              </span>
            </div>

            <div class="flex items-center gap-2">
              <slot name="header-btns"></slot>

              <v-spacer></v-spacer>
              <button
                  v-if="showClearBtn"
                  @click.prevent="emit('clearForm')"
                  class="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                  title="ایجاد فرم جدید"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
              </button>

              <!-- List Button -->
              <router-link
                  v-if="showListBtn"
                  :to="listRoute"
                  class="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                  title="لیست"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              </router-link>

              <!-- Export Buttons -->
              <template v-if="hasExport">
                <a
                    :href="printUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                    title="چاپ"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                  </svg>
                </a>

                <a
                    :href="pdfUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                    title="خروجی PDF"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                  </svg>
                </a>

                <button
                    @click="downloadUrl(excelUrl)"
                    class="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                    title="خروجی اکسل"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </button>
              </template>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="p-4">
            <slot name="default"></slot>
          </div>

          <!-- Form Actions -->
          <div v-if="showActions" class="px-4 py-3 border-t border-gray-100 bg-gray-50">
            <div class="flex flex-col-reverse md:flex-row justify-between items-center gap-4">
              <!-- Navigation Buttons -->
              <div v-if="showNavigationBtns" class="flex items-center gap-2">
                <button
                    @click="goToForm('first')"
                    :disabled="!hasFirst"
                    class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="اولین"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
                  </svg>
                </button>

                <button
                    @click="goToForm('prev')"
                    :disabled="!hasPrev"
                    class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="قبلی"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>

                <button
                    @click="goToForm('next')"
                    :disabled="!hasNext"
                    class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="بعدی"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>

                <button
                    @click="goToForm('last')"
                    :disabled="!hasLast"
                    class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="آخرین"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>

              <!-- Action Buttons -->
              <div class="flex flex-wrap items-center justify-center gap-2">
                <template v-if="isEditing">
                  <!-- Delete Button -->
                  <button
                      @click="delete_dialog = true"
                      v-if="canDelete"
                      class="px-4 text-white py-2 rounded-lg text-sm font-bold shadow-md transition-all duration-300 transform hover:scale-105  bg-gradient-to-r from-red-500 via-red-600 to-red-400 hover:bg-[#F17359]" >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>

                  <!-- Submit Button -->
                  <button
                      @click="submit(false)"
                      v-if="canSubmit"
                      class="px-4 text-white py-2 rounded-lg text-sm font-bold shadow-md transition-all duration-300 transform hover:scale-105  bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:bg-[#F17359]"        >
                    ثبت
                  </button>

                  <!-- Submit and Clear Button -->
                  <button
                      v-if="showSubmitAndClearBtn && canSubmit"
                      @click="submit(true)"
                      class=" px-4 text-white py-2 rounded-lg text-sm font-bold shadow-md transition-all duration-300 transform hover:scale-105  bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:bg-[#F17359]"        >
                  >
                    ثبت و صدور جدید
                  </button>

                  <slot name="footer-btns"></slot>
                </template>

                <!-- Edit Button -->
                <button
                    v-else-if="canEdit && !item.is_system_predefined"
                    @click="edit()"
                    class=" px-4 text-white py-2 rounded-lg text-sm font-bold shadow-md transition-all duration-300 transform hover:scale-105  bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 hover:bg-[#F17359]"        >
                  ویرایش
                </button>

                <!-- Define Button -->
                <button
                    v-if="isDefinable"
                    @click="parentCall('defineItem', [false])"
                    :disabled="!canDefine"
                    class="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ item.is_defined ? "قطعی شده" : "قطعی کردن" }}
                </button>

                <!-- Lock Button -->
                <button
                    v-if="hasLock"
                    @click="parentCall('toggleItemLock')"
                    :class="isLocked ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'"
                    class="p-2 rounded-lg text-white"
                    :title="isLocked ? 'باز کردن قفل' : 'قفل کردن'"
                >
                  <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                  >
                    <path
                        v-if="isLocked"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                    />
                    <path
                        v-else
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </button>

                <slot name="footer-outside-btns"></slot>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
          v-if="showList"
          class="w-full"
          :class="{
          'lg:w-1/2': !fullWidth,
          'lg:w-full': fullWidth
        }"
      >
        <m-datatable
            :show-export-btns="showListExportBtn"
            :height="500"
            :show-filter-row="false"
            :headers="cols"
            :export-url="listExportBaseUrl"
            :api-url="apiUrl"
            :items="items"
            @click:row="rowClick"
            v-on="listeners"
            v-bind="$attrs"
            :filters.sync="list_filters"
            :show-select="false"
            ref="importedDatatable"
        >
          <template v-for="(index, name) in $slots" v-slot:[name]>
            <slot :name="name"/>
          </template>
          <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
            <slot :name="name" v-bind="data"></slot>
          </template>
        </m-datatable>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="delete_dialog" max-width="400px">
      <v-card class="rounded-xl">
        <v-card-title class="text-red-600 font-medium">آیا اطمینان دارید؟</v-card-title>
        <v-card-text class="text-gray-700">
          با توجه به اینکه امکان بازگردانی اطلاعات پس از حذف نمی باشد، آیا از حذف اطمینان دارید؟
        </v-card-text>
        <v-card-actions class="justify-end gap-2 pb-4 pr-4">
          <button
              @click="delete_dialog = false"
              class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            خیر
          </button>
          <button
              @click="deleteForm"
              class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
          >
            حذف شود
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MDatatable from "@/components/m-datatable";

export default {
  name: "MForm",
  components: {MDatatable},
  props: {
    showHeader: {
      default: true,
    },
    title: {
      required: true,
    },
    showListBtn: {
      default: false,
    },
    listRoute: {
      default: undefined,
    },
    showClearBtn: {
      default: true,
    },
    exportBaseUrl: {},
    listExportBaseUrl: {},
    exportParams: {
      default: () => {
      },
    },
    showList: {
      default: true,
    },
    showListExportBtn: {
      default: true,
    },
    items: {
      default: undefined,
    },
    apiUrl: {
      default: undefined,
    },
    cols: {
      default: () => [],
    },
    showActions: {
      default: true,
    },
    showNavigationBtns: {
      default: true,
    },
    hasFirst: {
      default: true,
    },
    hasPrev: {
      default: true,
    },
    hasNext: {
      default: true,
    },
    hasLast: {
      default: true,
    },
    canSubmit: {
      default: true,
    },
    canDelete: {
      default: true,
    },
    canEdit: {
      default: true,
    },
    isEditing: {
      default: false,
    },
    showSubmitAndClearBtn: {
      default: true,
    },
    showCreationData: {
      default: true,
    },
    fullWidth: {
      default: false,
    },
    list_filters: {
      default: () => {
      },
    },
  },
  data() {
    return {
      delete_dialog: false,
    };
  },
  computed: {
    isXs() {
      return window.innerWidth <= 600;
    },
    listeners() {
      let listeners = this.$listeners;
      if (Object.keys(listeners).includes("click:row")) listeners["click:row"] = this.rowClick;
      return listeners;
    },
    hasFinancialYear() {
      return true
    },
    hasExport() {
      return this.exportBaseUrl != undefined && this.exportParams.id != undefined;
    },
    printUrl() {
      if (!this.hasExport) return "";
      let path = this.exportBaseUrl + "/html?";
      path = this.addParams(path);
      return this.endpoint(path);
    },
    pdfUrl() {
      if (!this.hasExport) return "";
      let path = this.exportBaseUrl + "/pdf?";
      path = this.addParams(path);
      return this.endpoint(path);
    },
    excelUrl() {
      if (!this.hasExport) return "";
      let path = this.exportBaseUrl + "/xlsx?";
      path = this.addParams(path);
      return this.endpoint(path);
    },
    isDefinable() {
      return this.canEdit && this.$parent.isDefinable && this.$parent.item.id && !this.isEditing;
    },
    canDefine() {
      return !this.$parent.item.is_defined;
    },
    hasLock() {
      return this.$parent.hasLock && this.$parent.item.id;
    },
    isLocked() {
      return this.$parent.item.is_locked;
    },
    parent() {
      return this.$parent;
    },
    item() {
      return this.$parent.item;
    },
  },
  mounted() {
    window.addEventListener("keyup", (event) => {
      if (this.isEditing && event.key == "Enter") {
        this.submit(false);
      }
    });
  },
  methods: {
    deleteForm() {
      this.emitDelete();
      this.delete_dialog = false;
    },
    emit(event) {
      this.$emit(event);
    },
    addParams(url) {
      if (this.listRoute && this.listRoute.params) {
        Object.keys(this.listRoute.params).forEach((k) => {
          url += k + "=" + this.listRoute.params[k] + "&";
        });
      }
      Object.keys(this.exportParams).forEach((k) => {
        url += k + "=" + this.exportParams[k] + "&";
      });
      url += "token=" + this.token;
      return url;
    },
    edit() {
      this.$emit("update:is-editing", true);
    },
    goToForm(pos) {
      this.$emit("goToForm", pos);
    },
    submit(clearForm) {
      this.$emit("submit", clearForm);
    },
    emitDelete() {
      this.$emit("delete");
      setTimeout(() => {
        this.$refs.importedDatatable.getDataFromApi()
      }, 1000);

    },
    emitDefine(indefine) {
      this.$emit("define");
      this.$parent.defineItem;
    },
    rowClick(item) {
      this.$emit("update:is-editing", false);
      this.$emit("click:row", item);
    },
    parentCall(methodName, params = []) {
      this.$parent[methodName](...params);
    },
  },
};
</script>

<style>
/* Custom Vuetify overrides to work with Tailwind */
.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  @apply bg-gray-50 text-gray-700 font-medium;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  @apply border-t border-gray-100;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  @apply bg-gray-50 cursor-pointer;
}

.v-menu__content {
  @apply shadow-lg rounded-lg border border-gray-200 overflow-hidden;
}

.v-text-field.v-text-field--enclosed .v-text-field__details {
  @apply mb-0;
}

.v-input__slot {
  @apply min-h-[40px] shadow-none;
}

.v-text-field--outlined fieldset {
  @apply border-gray-200;
}

.v-text-field--outlined.v-input--is-focused fieldset {
  @apply border-blue-500 border;
}

.v-list-item--link:hover {
  @apply bg-gray-50;
}

/* Form field styling */
.form-fields .v-input {
  font-size: 0.75rem !important;
}

.form-fields input {
  font-size: 0.75rem !important;
  padding: 0.125rem 0 !important;
}

.form-fields label {
  font-size: 0.8125rem !important;
}

.form-fields table th,
.form-fields table td {
  text-align: center !important;
  padding: 0.25rem !important;
  height: 2rem !important;
}

.form-fields table th {
  background-color: #ffffff !important;
}

.form-fields .v-text-field--filled.v-input--dense .v-input__slot,
.form-fields .v-text-field--outlined.v-input--dense .v-input__slot {
  min-height: 2rem !important;
}

.form-fields .v-text-field.v-text-field--enclosed .v-text-field__details,
.form-fields .v-text-field.v-text-field--enclosed > .v-input__control > .v-input__slot {
  padding: 0 0.25rem !important;
}
</style>